import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, keyframes, useTheme } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';

import { StatusChip } from '@components/StatusChip';
import { CELL_STATE_COLORS, RACK_THEME } from '../../constants';
import { formatPositionLabel } from '../../utils';

import type { RackCell as RackCellType } from '../../types';

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

type Props = {
  cell: RackCellType;
  isSelected?: boolean;
  onClick?: (cell: RackCellType) => void;
  isReadOnly?: boolean;
};

const RackCell: React.FC<Props> = ({
  cell,
  isSelected = false,
  onClick,
  isReadOnly = false
}) => {
  const { t } = useTranslation('workOrderDetails');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging
  } = useDraggable({
    id: cell.sequenceNumber,
    disabled: isReadOnly
  });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: cell.sequenceNumber
  });

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      setDraggableRef(node);
      setDroppableRef(node);
    },
    [setDraggableRef, setDroppableRef]
  );

  const handleClick = useCallback(() => {
    onClick?.(cell);
  }, [onClick, cell]);

  const colors = CELL_STATE_COLORS[cell.cellState];
  const borderColor = isDarkMode ? colors.border.dark : colors.border.light;
  const bgColor = isDarkMode ? colors.background.dark : colors.background.light;
  const isEmpty = cell.cellState === 'empty';
  const isInProgress = cell.cellState === 'in-progress';

  return (
    <Box
      ref={setRef}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0.5,
        p: 1.5,
        minHeight: 100,
        borderRadius: '4px',
        border: `2px solid ${borderColor}`,
        backgroundColor: isEmpty
          ? isDarkMode
            ? RACK_THEME.frameBg.dark
            : RACK_THEME.frameBg.light
          : bgColor,
        boxShadow:
          [
            'inset 0 2px 4px rgba(0,0,0,0.15)',
            isSelected ? `0 0 0 3px ${isDarkMode ? '#FBBF24' : '#D97706'}` : '',
            !isEmpty && !isSelected ? `0 0 6px ${borderColor}40` : ''
          ]
            .filter(Boolean)
            .join(', ') || 'none',
        opacity: isDragging ? 0.4 : 1,
        cursor: isReadOnly ? 'default' : 'pointer',
        transition:
          'box-shadow 0.15s ease, opacity 0.15s ease, border-color 0.15s ease',
        ...(isOver &&
          !isDragging && {
            borderStyle: 'dashed',
            backgroundColor: isDarkMode
              ? 'rgba(251, 191, 36, 0.08)'
              : 'rgba(217, 119, 6, 0.06)'
          }),
        ...(isInProgress && {
          animation: `${pulseAnimation} 2s ease-in-out infinite`
        })
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          color: isDarkMode
            ? RACK_THEME.labelColor.dark
            : RACK_THEME.labelColor.light,
          fontSize: '0.65rem',
          lineHeight: 1
        }}
      >
        {formatPositionLabel(cell.sequenceNumber)}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={isEmpty ? 400 : 700}
        sx={{
          color: isEmpty
            ? isDarkMode
              ? RACK_THEME.labelColor.dark
              : RACK_THEME.labelColor.light
            : 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          textAlign: 'center'
        }}
      >
        {isEmpty
          ? t('rack.emptyCell')
          : cell.detail?.id || t('equipment.noAssetId')}
      </Typography>

      {!isEmpty && cell.detail?.status && (
        <StatusChip
          label={cell.detail.status}
          variant="status"
          size="small"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      )}

      {!isEmpty &&
        (cell.resolution?.resolvedScript ? (
          <Typography
            variant="caption"
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              fontSize: '0.6rem',
              lineHeight: 1.2,
              color: cell.resolution.overridden ? 'info.main' : 'success.main'
            }}
          >
            {cell.resolution.resolvedScript.globalId}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              lineHeight: 1.2,
              color: 'warning.main'
            }}
          >
            {t('rack.unresolved')}
          </Typography>
        ))}
    </Box>
  );
};

export default RackCell;
